import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LegalFooter from '../components/LegalFooter';
import { toggleTheme } from '../lib/theme';
import { Mail, Lock, Eye, EyeOff, LogIn, Moon, Sun } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const { t, isRTL, toggleLanguage, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  // Where the user was headed before being bounced to /login by ProtectedRoute
  const intendedPath = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/dashboard';
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({});

  // Show "session expired" toast when redirected here by the 401 interceptor
  useEffect(() => {
    let expired = false;
    try {
      if (sessionStorage.getItem('mid2bid.sessionExpired') === '1') {
        expired = true;
        sessionStorage.removeItem('mid2bid.sessionExpired');
      }
    } catch (_) { /* ignore */ }
    if (expired) {
      toast.error('Your session has expired. Please log in again.', {
        duration: 6000,
        id: 'session-expired'
      });
    }
  }, []);

  const toggleDarkMode = () => {
    const next = toggleTheme();
    setDarkMode(next === 'dark');
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate(intendedPath, { replace: true });
    } catch (err) {
      const m = err.message || '';
      if (m.includes('verify your email')) {
        toast.error(t.login.pendingVerification);
        navigate('/verify', { state: { email: formData.email } });
      } else if (m.includes('re-verification')) {
        toast.error(t.login.reverifyRequired);
      } else if (m.includes('Invalid email or password')) {
        toast.error(t.login.invalidCredentials);
      } else {
        // WO-03 item 3 — throttle / rate-limit / suspended messages must reach
        // the customer verbatim, never be masked as "wrong password".
        toast.error(m || t.login.invalidCredentials);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className={`min-h-screen bg-background flex items-center justify-center p-4 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5 pointer-events-none"></div>
      
      {/* Language & Theme Toggle */}
      <div className={`fixed top-4 ${isRTL ? 'left-4' : 'right-4'} flex items-center gap-2 z-50`}>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleLanguage}
          className="rounded-full"
          data-testid="language-toggle"
        >
          {language === 'en' ? 'عربي' : 'EN'}
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleDarkMode}
          className="rounded-full"
          data-testid="theme-toggle"
        >
          {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Mid2Bid" className="h-12 mx-auto" />
        </div>

        <Card className="border-2 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <LogIn className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold" style={{fontFamily: "'Sora', sans-serif"}}>
              {t.login.title}
            </CardTitle>
            <CardDescription>
              {t.login.subtitle}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className={isRTL ? 'text-right block' : ''}>
                    {t.login.email}
                  </Label>
                  <div className="relative">
                    <Mail className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder={t.login.emailPlaceholder}
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className={`${isRTL ? 'pr-10' : 'pl-10'} h-12`}
                      dir="ltr"
                      data-testid="email-input"
                    />
                  </div>
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password" className={isRTL ? 'text-right block' : ''}>
                    {t.login.password}
                  </Label>
                  <div className="relative">
                    <Lock className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder={t.login.passwordPlaceholder}
                      value={formData.password}
                      onChange={(e) => updateField('password', e.target.value)}
                      className={`${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12`}
                      dir="ltr"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} text-muted-foreground hover:text-foreground`}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                </div>

                {/* Forgot password (DD-report B-10: unsupported Remember-Me removed) */}
                <div className={`flex items-center justify-end ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    {t.login.forgotPassword}
                  </Link>
                </div>

                <Button 
                  type="submit" 
                  className={`w-full h-12 text-lg font-semibold mt-6 ${isRTL ? 'flex-row-reverse' : ''}`}
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? t.login.loggingIn : t.login.login}
                  {!loading && <LogIn className={`${isRTL ? 'mr-2' : 'ml-2'} w-5 h-5`} />}
                </Button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t.login.noAccount}{' '}
                <Link to="/signup" className="text-primary font-semibold hover:underline" data-testid="signup-link">
                  {t.login.signup}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
        <LegalFooter language={language} />
      </div>
    </div>
  );
}
